import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { WorkflowCrmActionApiService } from '../services/workflow-crm-action-api.service';
import {
  WorkflowCrmActionType,
  WorkflowCrmActionTestResponse,
  AorRule
} from '../models/workflow-crm-action.model';
import { EntitySelectComponent, EntitySelectPage } from '../../../../shared/components/entity-select/entity-select.component';
import { WorkflowRecordSelect } from '../shared/workflow-record-select.helper';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { ContactoApiService } from '../../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../../empresas/services/empresa-api.service';
import { DealApiService } from '../../../deals/services/deal-api.service';
import { QuoteApiService } from '../../../quotes/services/quote-api.service';
import { TicketsApiService } from '../../../tickets/services/tickets-api.service';
import {
  DirectoryUser,
  UserDirectoryApiService,
  directoryUserLabel,
} from '../../../../shared/services/user-directory-api.service';
import { WorkflowApiService } from '../services/workflow-api.service';
import { WorkflowResponse, WorkflowObjectType } from '../models/workflow.model';
import { SegmentService } from '../../../segments/services/segment-api.service';
import { Segment } from '../../../segments/models/segment.model';
import { WorkflowPropertySelectComponent } from '../workflow-builder/components/inspector/node-config/property-select.component';
import {
  buildPropertyOptions,
  findPropertyDescriptor,
  WorkflowPropertyOption
} from '../workflow-builder/models/workflow-variable-catalog';
import { WorkflowConditionValueComponent } from '../workflow-builder/components/inspector/node-config/condition-value.component';
import { conditionOperatorOptionsFor } from '../workflow-builder/components/inspector/node-config/config-options';
import { workflowRecordLabel, workflowStatusLabel } from '../models/workflow-labels';

@Component({
  selector: 'app-workflow-crm-action-tester',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, EntitySelectComponent, WorkflowPropertySelectComponent,
    WorkflowConditionValueComponent],
  templateUrl: './workflow-crm-action-tester.component.html'
})
export class WorkflowCrmActionTesterComponent implements OnInit, OnDestroy {
  actionType: WorkflowCrmActionType = 'SET_PROPERTY_VALUE';
  objectType = 'CONTACT';
  objectId: number | null = null;
  /** Datos de contexto (JSON) opcionales para sustituir tokens {{...}} en la prueba. */
  contextJson = '';
  showContext = false;

  recordSelect!: WorkflowRecordSelect;
  recordDisplayText = '';

  loading = false;
  error = '';
  result: WorkflowCrmActionTestResponse | null = null;

  // ── SET_PROPERTY_VALUE ──────────────────────────────────────────────────
  spvProperty = 'phone';
  spvOperation = 'SET';
  spvValue = '';
  spvContinueOnError = false;

  // ── COPY_PROPERTY_VALUE ─────────────────────────────────────────────────
  cpvSourceType = 'CONTEXT';
  cpvSourceProperty = '';
  cpvTargetProperty = '';
  cpvContinueOnError = false;

  // ── CREATE_RECORD ───────────────────────────────────────────────────────
  crObjectType = 'DEAL';
  crFields: { property: string; value: string }[] = [{ property: 'name', value: 'Nuevo negocio - {{contact.firstname}}' }];
  crAssociate = true;
  crContinueOnError = false;

  // ── ASSOCIATE_RECORDS ───────────────────────────────────────────────────
  arTargetObjectType = 'COMPANY';
  arTargetObjectId: number | null = null;
  arTargetName = '';
  arAssociationType = 'PRIMARY_COMPANY';
  arContinueOnError = false;
  arTargetSelect!: WorkflowRecordSelect;

  // ── ASSIGN_OWNER_ROUND_ROBIN ────────────────────────────────────────────
  aorrUserIdList: number[] = [];
  aorrStateKey = '';
  aorrContinueOnError = false;

  // ── ASSIGN_OWNER_ROUTING ────────────────────────────────────────────────
  aorRules: AorRule[] = [{ property: 'country', operator: 'EQUALS', value: 'Peru', ownerUserId: null }];
  aorFallbackOwnerId: number | null = null;
  aorContinueOnError = false;

  // ── ADD/REMOVE_STATIC_LIST ──────────────────────────────────────────────
  listType = 'CONTACT_SEGMENT';
  listId: number | null = null;
  listContinueOnError = false;

  // ── ENROLL_IN_WORKFLOW ──────────────────────────────────────────────────
  enrollTargetWorkflowId: number | null = null;
  enrollTargetVersionId: number | null = null;
  enrollWorkflowName = '';
  enrollObjectType = 'CONTACT';
  enrollObjectIdSource = 'CURRENT_RECORD';
  enrollContinueOnError = false;

  // ── UNENROLL_FROM_WORKFLOW ──────────────────────────────────────────────
  unenrollTargetWorkflowId: number | null = null;
  unenrollWorkflowName = '';
  unenrollObjectType = 'CONTACT';
  unenrollReason = '';
  unenrollContinueOnError = false;

  // ── DELETE_RECORD ───────────────────────────────────────────────────────
  deleteContinueOnError = false;

  readonly actionTypeOptions: { value: WorkflowCrmActionType; label: string }[] = [
    { value: 'SET_PROPERTY_VALUE',       label: 'Establecer valor de propiedad' },
    { value: 'COPY_PROPERTY_VALUE',      label: 'Copiar valor de propiedad' },
    { value: 'CREATE_RECORD',            label: 'Crear registro' },
    { value: 'ASSOCIATE_RECORDS',        label: 'Asociar registros' },
    { value: 'ASSIGN_OWNER_ROUND_ROBIN', label: 'Asignar responsable por reparto rotativo' },
    { value: 'ASSIGN_OWNER_ROUTING',     label: 'Asignar responsable por reglas' },
    { value: 'ADD_TO_STATIC_LIST',       label: 'Agregar a lista estática' },
    { value: 'REMOVE_FROM_STATIC_LIST',  label: 'Eliminar de lista estática' },
    { value: 'ENROLL_IN_WORKFLOW',       label: 'Inscribir en workflow' },
    { value: 'UNENROLL_FROM_WORKFLOW',   label: 'Desinscribir de workflow' },
    { value: 'DELETE_RECORD',            label: 'Eliminar registro' }
  ];

  // INC_CRM_020 (8) inc26-46 — `value` = clave exacta del backend, `label` = único texto traducible.
  // Listas propias del probador: `objectTypeOptions` admite SUBSCRIPTION y `conditionOperatorOptions`
  // es un subconjunto de los operadores del constructor, así que no se comparten con el builder.
  readonly objectTypeOptions: { value: string; label: string }[] = [
    { value: 'CONTACT', label: 'Contacto' },
    { value: 'COMPANY', label: 'Empresa' },
    { value: 'DEAL', label: 'Negocio' },
    { value: 'QUOTE', label: 'Cotización' },
    { value: 'TICKET', label: 'Ticket' }
  ];
  readonly crmObjectTypeOptions: { value: string; label: string }[] = [
    { value: 'CONTACT', label: 'Contacto' },
    { value: 'COMPANY', label: 'Empresa' },
    { value: 'DEAL', label: 'Negocio' },
    { value: 'QUOTE', label: 'Cotización' }
  ];
  readonly spvOperationOptions: { value: string; label: string }[] = [
    { value: 'SET', label: 'Asignar un valor' },
    { value: 'CLEAR', label: 'Vaciar la propiedad' },
    { value: 'COPY_FROM_PROPERTY', label: 'Copiar desde otra propiedad' },
    { value: 'INCREMENT', label: 'Aumentar (sumar)' },
    { value: 'DECREMENT', label: 'Disminuir (restar)' },
    { value: 'FORMULA', label: 'Calcular con una fórmula' }
  ];
  readonly sourceTypeOptions: { value: string; label: string }[] = [
    { value: 'CONTEXT', label: 'Contexto del workflow' },
    { value: 'PAYLOAD', label: 'Datos del evento' },
    { value: 'CURRENT_RECORD', label: 'El registro inscrito en el workflow' },
    { value: 'ASSOCIATED_RECORD', label: 'Un registro asociado' },
    { value: 'EXPLICIT_RECORD', label: 'Un registro concreto que yo elija' }
  ];
  readonly listTypeOptions: { value: string; label: string }[] = [
    { value: 'CONTACT_SEGMENT', label: 'Lista de contactos' },
    { value: 'COMPANY_SEGMENT', label: 'Lista de empresas' },
    { value: 'DEAL_SEGMENT', label: 'Lista de negocios' }
  ];
  readonly enrollObjectIdSourceOptions: { value: string; label: string }[] = [
    { value: 'CURRENT_RECORD', label: 'El registro inscrito en este workflow' },
    { value: 'EXPLICIT', label: 'Un registro concreto que yo elija' }
  ];
  readonly conditionOperatorOptions: { value: string; label: string }[] = [
    { value: 'EQUALS', label: 'Es igual a' },
    { value: 'NOT_EQUALS', label: 'Es distinto de' },
    { value: 'CONTAINS', label: 'Contiene' },
    { value: 'GREATER_THAN', label: 'Es mayor que' },
    { value: 'LESS_THAN', label: 'Es menor que' },
    { value: 'IS_EMPTY', label: 'Está vacío' },
    { value: 'IS_NOT_EMPTY', label: 'No está vacío' }
  ];

  allUsers: DirectoryUser[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private crmActionApi: WorkflowCrmActionApiService,
    private authService: AuthService,
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private dealApi: DealApiService,
    private quoteApi: QuoteApiService,
    private ticketsApi: TicketsApiService,
    private userDirectory: UserDirectoryApiService,
    private workflowApi: WorkflowApiService,
    private segmentService: SegmentService
  ) {}

  // ── Selector de lista estática (= segmento STATIC) por nombre (GAP 2) ──────
  listName = '';
  listSearch = (term: string, page: number): Observable<EntitySelectPage<Segment>> => {
    return this.segmentService.getSegments(page, 10, { type: 'STATIC', searchTerm: term }).pipe(
      map((r) => ({ items: r.content, totalPages: r.totalPages })),
      catchError(() => of({ items: [] as Segment[], totalPages: 0 }))
    );
  };
  segmentLabel = (s: Segment): string => s.name;
  onListSelected(s: Segment | null): void { this.listName = s?.name ?? ''; }

  ngOnInit(): void {
    const businessId = () => this.authService.getCurrentUser()?.businessId || 1;
    this.recordSelect = new WorkflowRecordSelect(
      this.contactoApi, this.empresaApi, this.dealApi, this.quoteApi, this.ticketsApi,
      () => this.objectType, businessId
    );
    this.arTargetSelect = new WorkflowRecordSelect(
      this.contactoApi, this.empresaApi, this.dealApi, this.quoteApi, this.ticketsApi,
      () => this.arTargetObjectType, businessId
    );
    // Directorio de la empresa: sin `users:VIEW` y con el negocio resuelto desde el token.
    this.userDirectory.searchUsers('', 200).subscribe({
      next: (users) => { this.allUsers = users; },
      error: () => {}
    });
  }

  onObjectTypeChange(): void {
    this.objectId = null;
    this.recordDisplayText = '';
  }

  onRecordSelected(item: any | null): void {
    this.recordDisplayText = item ? this.recordSelect.label(item) : '';
  }

  onArTargetSelected(item: any | null): void {
    this.arTargetName = item ? this.arTargetSelect.label(item) : '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Buscador de usuarios ──────────────────────────────────────────────────
  userSearch = (term: string): Observable<EntitySelectPage<DirectoryUser>> =>
    this.userDirectory.searchUsers(term, 20).pipe(
      map((items) => ({ items, totalPages: 1 })),
      catchError(() => of({ items: [] as DirectoryUser[], totalPages: 0 }))
    );
  userLabel = (u: DirectoryUser): string => directoryUserLabel(u);
  userSublabel = (u: DirectoryUser): string => u.mail ?? '';
  userNameById(id: number | null | undefined): string {
    if (id == null) return '';
    const u = this.allUsers.find((x) => x.id === id);
    // La lista solo trae ACTIVOS: no estar aquí significa dado de baja, no «#id».
    return u ? this.userLabel(u) : 'Usuario no disponible';
  }

  // ── Buscador de workflow destino ─────────────────────────────────────────
  workflowSearch = (term: string, page: number): Observable<EntitySelectPage<WorkflowResponse>> => {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    return this.workflowApi.getWorkflows({ businessId, page, size: 10, searchTerm: term || undefined }).pipe(
      map((res) => Array.isArray(res)
        ? { items: res, totalPages: 1 }
        : { items: res.content, totalPages: res.totalPages }),
      catchError(() => of({ items: [] as WorkflowResponse[], totalPages: 0 }))
    );
  };
  workflowLabel = (w: WorkflowResponse): string => w.name;
  onEnrollWorkflowSelected(w: WorkflowResponse | null): void {
    this.enrollWorkflowName = w?.name ?? '';
    this.enrollTargetVersionId = w?.publishedVersionId ?? null;
  }
  onUnenrollWorkflowSelected(w: WorkflowResponse | null): void {
    this.unenrollWorkflowName = w?.name ?? '';
  }

  // ── Round-robin (chips) ──────────────────────────────────────────────────
  addRoundRobinUser(id: number | null): void {
    if (id == null || this.aorrUserIdList.includes(id)) return;
    this.aorrUserIdList = [...this.aorrUserIdList, id];
  }
  removeRoundRobinUser(id: number): void {
    this.aorrUserIdList = this.aorrUserIdList.filter((x) => x !== id);
  }

  // ── Routing rules ────────────────────────────────────────────────────────
  /** Propiedades sugeridas del registro actual (según objectType) para el selector de "Propiedad". */
  get routingPropertyOptions(): WorkflowPropertyOption[] {
    return buildPropertyOptions(this.objectType as WorkflowObjectType);
  }
  /**
   * Operadores que admite la propiedad elegida, igual que en el constructor. La lista propia del
   * probador (`conditionOperatorOptions`) ofrecía «es mayor que» para una empresa o un estado, que
   * es una regla que no puede cumplirse nunca.
   */
  operatorOptionsFor(property: string, current: string): { value: string; label: string }[] {
    const descriptor = findPropertyDescriptor(this.routingPropertyOptions, property);
    return [...conditionOperatorOptionsFor(descriptor?.kind ?? 'TEXT', current)];
  }

  addAorRule(): void { this.aorRules = [...this.aorRules, { property: '', operator: 'EQUALS', value: '', ownerUserId: null }]; }
  removeAorRule(idx: number): void { this.aorRules = this.aorRules.filter((_, i) => i !== idx); }

  // ── CREATE_RECORD fields ─────────────────────────────────────────────────
  addCrField(): void { this.crFields = [...this.crFields, { property: '', value: '' }]; }
  removeCrField(idx: number): void { this.crFields = this.crFields.filter((_, i) => i !== idx); }

  /** Construye el config completo (todos los campos requeridos) según el tipo de acción. */
  private buildConfig(): Record<string, any> {
    switch (this.actionType) {
      case 'SET_PROPERTY_VALUE':
        return {
          target: { type: 'CURRENT_RECORD' },
          property: this.spvProperty,
          operation: this.spvOperation,
          value: this.spvValue,
          continueOnError: this.spvContinueOnError
        };
      case 'COPY_PROPERTY_VALUE':
        return {
          source: { sourceType: this.cpvSourceType, property: this.cpvSourceProperty },
          target: { type: 'CURRENT_RECORD', property: this.cpvTargetProperty },
          continueOnError: this.cpvContinueOnError
        };
      case 'CREATE_RECORD': {
        const fields: Record<string, any> = {};
        for (const f of this.crFields) { if (f.property.trim()) fields[f.property.trim()] = f.value; }
        return {
          objectType: this.crObjectType,
          fields,
          associateWithCurrentRecord: this.crAssociate,
          continueOnError: this.crContinueOnError
        };
      }
      case 'ASSOCIATE_RECORDS':
        return {
          source: { type: 'CURRENT_RECORD' },
          target: { objectType: this.arTargetObjectType, objectId: this.arTargetObjectId },
          associationType: this.arAssociationType,
          continueOnError: this.arContinueOnError
        };
      case 'ASSIGN_OWNER_ROUND_ROBIN':
        return {
          userIds: this.aorrUserIdList,
          stateKey: this.aorrStateKey || undefined,
          continueOnError: this.aorrContinueOnError
        };
      case 'ASSIGN_OWNER_ROUTING':
        return {
          target: { type: 'CURRENT_RECORD' },
          rules: this.aorRules,
          fallbackOwnerUserId: this.aorFallbackOwnerId,
          continueOnError: this.aorContinueOnError
        };
      case 'ADD_TO_STATIC_LIST':
      case 'REMOVE_FROM_STATIC_LIST':
        return { listType: this.listType, listId: this.listId, continueOnError: this.listContinueOnError };
      case 'ENROLL_IN_WORKFLOW':
        return {
          targetWorkflowId: this.enrollTargetWorkflowId,
          targetWorkflowVersionId: this.enrollTargetVersionId,
          objectType: this.enrollObjectType,
          objectIdSource: this.enrollObjectIdSource,
          continueOnError: this.enrollContinueOnError
        };
      case 'UNENROLL_FROM_WORKFLOW':
        return {
          targetWorkflowId: this.unenrollTargetWorkflowId,
          objectType: this.unenrollObjectType,
          reason: this.unenrollReason || undefined,
          continueOnError: this.unenrollContinueOnError
        };
      case 'DELETE_RECORD':
        return { continueOnError: this.deleteContinueOnError };
      default:
        return {};
    }
  }

  test(): void {
    let context: Record<string, any> = {};
    if (this.contextJson.trim()) {
      try { context = JSON.parse(this.contextJson); }
      catch { this.error = 'Los datos de contexto no son un JSON válido.'; return; }
    }

    this.loading = true;
    this.error = '';
    this.result = null;

    this.crmActionApi.test({
      actionType: this.actionType,
      objectType: this.objectType,
      ...(this.objectId ? { objectId: this.objectId } : {}),
      config: this.buildConfig(),
      context
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.result = res; this.loading = false; },
      error: (err) => {
        const msg = err.error?.message ?? '';
        if (err.status === 403) this.error = 'No tienes permisos para realizar esta acción.';
        else if (msg.toLowerCase().includes('ticket')) this.error = 'No se pudo procesar la solicitud sobre el ticket.';
        else if (msg.toLowerCase().includes('delete') || msg.toLowerCase().includes('dangerous')) this.error = err.error?.message ?? 'Acción peligrosa no permitida.';
        else this.error = msg || 'Error al ejecutar la acción CRM.';
        this.loading = false;
      }
    });
  }

  /**
   * INC_CRM_020 (8) inc26-46 — el badge pintaba el enum crudo del backend (SUCCESS/FAILED/SKIPPED)
   * y la fila de detalle el `actionType` técnico. El valor que llega NO se toca; solo se traduce
   * lo que se muestra, y si aparece un estado/tipo desconocido se enseña tal cual.
   */
  /** Nombre del registro afectado: antes se pintaba «CONTACT #42». */
  readonly recordText = workflowRecordLabel;

  /**
   * Estado en español. El vocabulario vive en el diccionario único del módulo
   * (`models/workflow-labels.ts`); antes cada probador repetía su propio `switch` de tres casos y
   * cualquier otro estado del motor (`RETRY_SCHEDULED`, `PENDING`…) se colaba en crudo.
   */
  statusLabel(status: string | undefined): string {
    return workflowStatusLabel(status, 'Desconocido');
  }

  actionTypeLabel(type: string | undefined): string {
    if (!type) {
      return '—';
    }
    return this.actionTypeOptions.find((o) => o.value === type)?.label ?? type;
  }

  getStatusClass(status: string | undefined): string {
    switch (status?.toUpperCase()) {
      case 'SUCCESS': return 'bg-green-100 text-green-700';
      case 'FAILED':  return 'bg-red-100 text-red-700';
      case 'SKIPPED': return 'bg-gray-100 text-gray-600';
      default:        return 'bg-blue-100 text-blue-700';
    }
  }

  get isDangerousAction(): boolean {
    return this.actionType === 'DELETE_RECORD';
  }

  get resultJson(): string {
    return JSON.stringify(this.result?.output ?? {}, null, 2);
  }

  get errorJson(): string {
    return JSON.stringify(this.result?.error ?? {}, null, 2);
  }

  // Helpers de visibilidad por acción (para el template)
  get isSetProperty(): boolean { return this.actionType === 'SET_PROPERTY_VALUE'; }
  get isCopyProperty(): boolean { return this.actionType === 'COPY_PROPERTY_VALUE'; }
  get isCreateRecord(): boolean { return this.actionType === 'CREATE_RECORD'; }
  get isAssociate(): boolean { return this.actionType === 'ASSOCIATE_RECORDS'; }
  get isRoundRobin(): boolean { return this.actionType === 'ASSIGN_OWNER_ROUND_ROBIN'; }
  get isRouting(): boolean { return this.actionType === 'ASSIGN_OWNER_ROUTING'; }
  get isStaticList(): boolean { return this.actionType === 'ADD_TO_STATIC_LIST' || this.actionType === 'REMOVE_FROM_STATIC_LIST'; }
  get isEnroll(): boolean { return this.actionType === 'ENROLL_IN_WORKFLOW'; }
  get isUnenroll(): boolean { return this.actionType === 'UNENROLL_FROM_WORKFLOW'; }
  get isDelete(): boolean { return this.actionType === 'DELETE_RECORD'; }
}
